import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { KEY_STATUSES } from "../lib/stages";
import { Building2, DoorClosed, Home } from "lucide-react";
import { StageBadge, FlagPip } from "../components/StageBadge";
import RoomDrawer from "../components/RoomDrawer";
import { useApplication } from "../context/ApplicationContext";

export default function Blocks() {
  const { user } = useAuth();
  const { effectiveWorkflow } = useApplication();
  const [blocks, setBlocks] = useState([]);
  const [active, setActive] = useState(null);
  const [tree, setTree] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkKeyStatus, setBulkKeyStatus] = useState(KEY_STATUSES[0]);
  const canBulkUpdate = user?.role === "admin" || user?.role === "staff";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/blocks");
      setBlocks(data);
      if (data[0]) setActive(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const { data } = await api.get(`/blocks/${active}/tree`);
      setTree(data);
    })();
  }, [active, selectedRoom]);

  useEffect(() => {
    setPicked(new Set());
    setBulkMode(false);
  }, [active]);

  const roomIds = useMemo(() => (tree?.flats || []).flatMap((flat) => flat.rooms.map((room) => room.id)), [tree]);
  const allPicked = roomIds.length > 0 && roomIds.every((id) => picked.has(id));

  const activeStagesForRoom = (room) => {
    const activeStages = Object.entries(room.stages || {})
      .filter(([, st]) => ["in_progress", "on_hold"].includes(st?.status))
      .map(([stage]) => stage);
    return activeStages.length > 0 ? activeStages : [room.current_stage].filter(Boolean);
  };

  const togglePick = (roomId) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const togglePickAll = () => {
    if (allPicked) setPicked(new Set());
    else setPicked(new Set(roomIds));
  };

  const applyBulkUpdate = async (patch) => {
    if (picked.size === 0) {
      toast.error("Pick at least one room");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/rooms/bulk-flags", { room_ids: Array.from(picked), ...patch });
      toast.success(`Updated ${data.updated} room${data.updated === 1 ? "" : "s"}`);
      setPicked(new Set());
      setBulkMode(false);
      const refreshed = await api.get(`/blocks/${active}/tree`);
      setTree(refreshed.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="blocks-page">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <h1 className="font-chivo font-black text-4xl tracking-tight">Blocks</h1>
        {canBulkUpdate && active && (
          <button
            onClick={() => { setBulkMode((v) => !v); setPicked(new Set()); }}
            data-testid="blocks-bulk-toggle"
            className={`px-3 py-2 text-xs font-mono font-bold uppercase border ${bulkMode ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"}`}
          >
            {bulkMode ? "Cancel bulk" : "Bulk select"}
          </button>
        )}
      </div>

      {bulkMode && canBulkUpdate && (
        <div className="bg-yellow-50 border border-yellow-300 p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="blocks-bulk-toolbar">
          <div className="text-xs font-mono font-bold uppercase tracking-wider text-yellow-900 pb-2">
            {picked.size} selected
          </div>
          <button onClick={togglePickAll} data-testid="blocks-bulk-pick-all" className="px-2 py-1 text-[10px] font-mono font-bold uppercase border border-gray-400 bg-white hover:border-black mb-1">
            {allPicked ? "Unpick all" : "Pick all in block"}
          </button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ rebooking: true })} data-testid="blocks-bulk-rebooker-on" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1">Mark Rebooker</button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ rebooking: false })} data-testid="blocks-bulk-rebooker-off" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-gray-400 bg-white hover:border-black disabled:opacity-40 mb-1">Clear Rebooker</button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ refurbishment: true })} data-testid="blocks-bulk-refurb-on" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1">Mark Refurbishment</button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ refurbishment: false })} data-testid="blocks-bulk-refurb-off" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-gray-400 bg-white hover:border-black disabled:opacity-40 mb-1">Clear Refurbishment</button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ summer_stay: true })} data-testid="blocks-bulk-summer-stay-on" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1">Mark Summer Stay</button>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ summer_stay: false })} data-testid="blocks-bulk-summer-stay-off" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-gray-400 bg-white hover:border-black disabled:opacity-40 mb-1">Clear Summer Stay</button>
          <label className="text-[10px] font-mono uppercase tracking-wider text-gray-600">
            Key status
            <select value={bulkKeyStatus} onChange={(e) => setBulkKeyStatus(e.target.value)} disabled={bulkBusy || picked.size === 0} data-testid="blocks-bulk-key-status-select" className="mt-1 block border border-gray-400 bg-white px-2 py-1 text-[10px] font-mono uppercase disabled:opacity-40">
              {KEY_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
          <button disabled={bulkBusy || picked.size === 0} onClick={() => applyBulkUpdate({ key_status: bulkKeyStatus })} data-testid="blocks-bulk-key-status-apply" className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white disabled:opacity-40 mb-1">Update key status</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div className="bg-white border border-gray-200">
          {blocks.map((b) => (
            <button
              key={b.id}
              onClick={() => setActive(b.id)}
              data-testid={`block-select-${b.code}`}
              className={`w-full text-left px-4 py-3 border-b border-gray-200 last:border-b-0 flex items-center gap-3 transition-colors ${
                active === b.id ? "bg-black text-white" : "hover:bg-gray-50"
              }`}
            >
              <Building2 size={16} />
              <div>
                <div className="font-chivo font-bold text-sm">{b.name}</div>
                <div
                  className={`text-[10px] font-mono uppercase ${
                    active === b.id ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {b.code}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div>
          {tree?.flats?.map((flat) => (
            <div
              key={flat.id}
              className="bg-white border border-gray-200 mb-4"
              data-testid={`flat-card-${flat.name.replace(/\s+/g, "-")}`}
            >
              <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
                <Home size={14} />
                <div className="font-chivo font-black text-lg">{flat.name}</div>
              </div>
              <div className="divide-y divide-gray-100">
                {flat.rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => bulkMode ? togglePick(room.id) : setSelectedRoom(room.id)}
                    data-testid={`room-row-${room.name.replace(/\s+/g, "-")}`}
                    className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {bulkMode ? (
                        <input
                          type="checkbox"
                          checked={picked.has(room.id)}
                          onChange={(e) => { e.stopPropagation(); togglePick(room.id); }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`blocks-bulk-room-${room.id}`}
                        />
                      ) : (
                        <DoorClosed size={14} className="text-gray-400" />
                      )}
                      <div>
                        <div className="font-chivo font-bold text-sm">
                          {room.name}
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 uppercase mt-0.5">
                          {room.type === "common"
                            ? "Common area"
                            : `${
                                room.bathroom === "ensuite"
                                  ? "Ensuite"
                                  : "Shared bath"
                              } · ${room.key_status}`}
                        </div>
                        {room.stages?.cleaning?.note?.toLowerCase().includes("reopened") && (
                          <div className="mt-1 text-[10px] font-mono border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5 inline-block">
                            Returned from cleaning inspection: {room.stages?.cleaning_inspection?.form_data?.fail_reason || "No reason entered"}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {room.flags?.rebooking && <FlagPip kind="rebooking" />}
                      {room.flags?.storage && <FlagPip kind="storage" />}
                      {room.flags?.summer_stay && <FlagPip kind="summer_stay" />}
                      {room.flags?.refurbishment && (
                        <FlagPip kind="refurbishment" />
                      )}
                      {activeStagesForRoom(room).map((stage) => (
                        <StageBadge key={stage} stage={stage} definition={effectiveWorkflow?.stages?.find(item => item.key === stage)} />
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedRoom && (
        <RoomDrawer
          roomId={selectedRoom}
          onClose={() => setSelectedRoom(null)}
        />
      )}
    </div>
  );
}
